/**
 * Type vocabulary for the runtime feature-flag catalog.
 *
 * This is pure, framework-agnostic TypeScript. It models the SHAPE of a flag
 * catalog — the names, the mechanism namespaces, the per-flag metadata — but it
 * does NO I/O. Reading an actual flag value at runtime is a separate concern
 * (`RuntimeConfigPort` + an AppConfig adapter, built in later tickets); nothing
 * here fetches, caches, or evaluates a value.
 *
 * See epic #274 and task #277.
 */

/**
 * The mechanism that reads a flag, encoded as the key prefix. The prefix — not a
 * hand-applied tag — derives both the read mechanism and the frontend/backend
 * surface of every flag:
 *
 * | Prefix       | Read by                     | Surface  |
 * |--------------|-----------------------------|----------|
 * | `routes.`    | inbound guard/interceptor   | backend  |
 * | `wiring.`    | composition root (pre-DI)   | backend  |
 * | `ui.`        | SPA at bootstrap            | frontend |
 * | `behavior.`  | a use case via a port       | backend  |
 */
export const FLAG_MECHANISM_PREFIXES = ['routes', 'wiring', 'ui', 'behavior'] as const;

/**
 * A known mechanism namespace, e.g. `'routes'`. The closed set is the single
 * source of truth for which prefixes are legal; an unknown prefix is both a
 * compile error (via {@link FlagKey}) and a build-time throw in `defineFlags` /
 * `composeCatalog`.
 */
export type FlagMechanismPrefix = (typeof FLAG_MECHANISM_PREFIXES)[number];

/**
 * The flag types reserved by the catalog vocabulary.
 *
 * - `'boolean'` — a binary on/off switch (the common kill-switch / dark-ship case).
 * - `'enum'`    — a value drawn from a fixed, closed set of options.
 * - `'variant'` — RESERVED for multi-variant (context-dependent) evaluation. No
 *   variant flag ships in v1; the type exists so the fence against it (see
 *   {@link VariantFlagConfig} and the `wiring.*` rejection in `defineFlags`) is in
 *   place from v1. Multi-variant evaluation is deferred to #287 (T12).
 */
export type FlagType = 'boolean' | 'enum' | 'variant';

/** Fields every catalog entry carries, regardless of {@link FlagType}. */
interface FlagConfigBase {
    /** Human-readable description of what the flag controls. Required. */
    readonly description: string;
}

/** A binary on/off flag. */
export interface BooleanFlagConfig extends FlagConfigBase {
    readonly type: 'boolean';
    /** The value served when no deployed value is found (source miss or unreachable). */
    readonly defaultValue: boolean;
}

/** A flag whose value is one of a fixed, closed set of string options. */
export interface EnumFlagConfig extends FlagConfigBase {
    readonly type: 'enum';
    /** The closed set of legal values. */
    readonly values: readonly string[];
    /** The value served by default; must be one of `values`. */
    readonly defaultValue: string;
}

/**
 * RESERVED multi-variant flag shape. Reserved from v1 so the fence exists before
 * the first variant flag does, but NO variant flag ships here — see #287 (T12).
 * `defineFlags` rejects this `type` on any `wiring.*` key at build time: a
 * one-way cutover must never become a runtime variant toggle.
 */
export interface VariantFlagConfig extends FlagConfigBase {
    readonly type: 'variant';
    /** The closed set of variant names. */
    readonly variants: readonly string[];
    /** The variant served when no evaluation context selects another. */
    readonly defaultVariant: string;
}

/** A single catalog entry — one of the supported flag shapes. */
export type FlagConfig = BooleanFlagConfig | EnumFlagConfig | VariantFlagConfig;

/**
 * Recursively mark every property (including nested arrays) `readonly`. A caller
 * may build its definitions with mutable fields (`values: string[]`); the catalog
 * `defineFlags` returns is deeply frozen at runtime, and this type makes the
 * COMPILE-TIME shape match that — so reading `catalog['ui.theme'].values` yields a
 * `readonly string[]`, never a mutable `string[]` that compiles but throws on push.
 */
export type DeepReadonly<T> = T extends (infer E)[]
    ? ReadonlyArray<DeepReadonly<E>>
    : T extends readonly (infer E)[]
      ? ReadonlyArray<DeepReadonly<E>>
      : T extends object
        ? { readonly [K in keyof T]: DeepReadonly<T[K]> }
        : T;

/**
 * A catalog entry as it is EXPOSED once stored: a {@link FlagConfig} with every
 * field — and every nested array — deeply `readonly`, matching the runtime freeze
 * applied by `defineFlags`. The public fragment/catalog types are keyed to this
 * normalized shape rather than to a caller's (possibly mutable) definition type.
 */
export type NormalizedFlagConfig = DeepReadonly<FlagConfig>;

/**
 * The short, unqualified flag names a context declares, mapped to their config.
 * `defineFlags` prefixes each name with the mechanism namespace to produce the
 * fully-qualified catalog keys (`routes.foo`, `ui.bar`, ...).
 */
export type FlagDefinitions = Readonly<Record<string, FlagConfig>>;

/**
 * The widest shape a catalog fragment can take: any `${Prefix}.${string}` key
 * mapped to a {@link FlagConfig}. This is the CONSTRAINT bound (what every
 * fragment is assignable to), not the type of a specific fragment — a fragment
 * built by `defineFlags` is narrowed to its exact declared keys via
 * {@link FlagCatalogFragmentOf}. Use this where a fragment of unknown shape is
 * accepted (e.g. the `composeCatalog` parameter bound).
 *
 * The key template is `${Prefix}.${string}`, so a key whose prefix is not a
 * known mechanism is a compile error, not just a runtime throw.
 */
export type FlagCatalogFragment<Prefix extends FlagMechanismPrefix = FlagMechanismPrefix> = Readonly<
    Record<`${Prefix}.${string}`, FlagConfig>
>;

/**
 * The precise type of the fragment `defineFlags(prefix, definitions)` returns:
 * each LITERAL short name in `Definitions` re-keyed under `Prefix`, preserving
 * the declared key set exactly rather than widening to every `${Prefix}.${string}`.
 *
 * This is what keeps `FlagKeyOf<typeof FLAG_CATALOG>` a CLOSED union of the keys
 * actually declared — an undeclared same-prefix key (`routes.not-declared`) does
 * not satisfy it.
 *
 * Each entry is exposed as a {@link NormalizedFlagConfig} (deeply `readonly`), so
 * the public type matches the runtime deep-freeze: a caller passing a mutable
 * `values: string[]` still reads `readonly string[]` off the fragment, never a
 * mutable array that compiles but throws when mutated.
 */
export type FlagCatalogFragmentOf<Prefix extends FlagMechanismPrefix, Definitions extends FlagDefinitions> = Readonly<{
    [Name in keyof Definitions & string as `${Prefix}.${Name}`]: DeepReadonly<Definitions[Name]>;
}>;
